/**
 * What the account row of the sidebar shows about the signed-in operator, from the BFF's verified session only: the
 * name and e-mail the login returned and the Cognito groups of the session. Nothing here is a default or a guess:
 * an unknown group has no role label and shows none.
 */

/** Role labels of the Cognito groups the BFF knows (`PLATFORM_ADMIN_GROUPS`), strongest first: the first one the operator has is shown. */
const ROLE_LABELS: readonly (readonly [group: string, label: string])[] = [
  ['tss-admin', 'Platform admin'],
  ['admin', 'Admin'],
  ['auth-admin', 'Auth admin'],
];

/** The label of the operator's role, or null when none of their groups is one we have a label for. */
export function roleLabelFromGroups(groups: readonly string[] | undefined): string | null {
  if (!groups) return null;
  return ROLE_LABELS.find(([group]) => groups.includes(group))?.[1] ?? null;
}

/** The separators of a name made from an e-mail's local part (`ana.lopez+ops` -> Ana, Lopez, Ops). */
const NAME_SEPARATORS = /[\s._+-]+/;
/** An account row's avatar shows at most this many initials. */
const MAX_INITIALS = 2;

/** The initials of a name or an e-mail (its local part): `Ana Lopez` -> "AL", `ana.lopez@x.com` -> "AL". Empty when it has no letter or digit. */
export function initialsOf(label: string): string {
  const base = label.includes('@') ? (label.split('@')[0] ?? '') : label;
  const initials = base
    .split(NAME_SEPARATORS)
    .map((part) => part.replace(/[^\p{L}\p{N}]/gu, '')[0] ?? '')
    .filter(Boolean)
    .slice(0, MAX_INITIALS)
    .join('');
  return initials.toUpperCase();
}
